import {
  FC,
  useRef,
  MutableRefObject,
  MouseEvent,
  TouchEvent
} from 'react';

import { TinyColor } from '../../../utils';

import { useDragListeners } from '../../../utils/useDragListeners';
import { TPropsComp, TCoords } from './types';

const Ribbon: FC<TPropsComp> = ({
  color,
  onChange,
  setChange
}) => {
  const node = useRef() as MutableRefObject<HTMLDivElement>;

  const drag = useDragListeners();

  const removeListeners = () => {
    drag.stop();
  };

  const removeTouchListeners = () => {
    setChange(false);
    drag.stop();
  };

  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });

    drag.start({ mousemove: onDrag, mouseup: onDragEnd });
  };

  const onDrag = (e: any) => {
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });
  };

  const onDragEnd = (e: any) => {
    const x = e.clientX;
    const y = e.clientY;

    pointMoveTo({
      x,
      y
    });

    setChange(false);

    removeListeners();
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    if (e.touches.length !== 1) {
      return;
    }

    removeTouchListeners();

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    pointMoveTo({ x, y });

    drag.start(
      { touchmove: onTouchMove, touchend: onTouchEnd },
      { passive: false }
    );
  };

  const onTouchMove = (e: any) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;

    pointMoveTo({
      x,
      y
    });
  };

  const onTouchEnd = () => {
    removeTouchListeners();
  };

  const pointMoveTo = (coords: TCoords) => {
    const rect = node.current?.getBoundingClientRect();
    if (!rect) return;

    const width = rect.width;
    let left = coords.x - rect.left;
    left = Math.max(0, left);
    left = Math.min(left, width);

    const huePercent = left / width;
    const hue = huePercent * 360;

    color.hue = hue;
    onChange(color);
  };

  const hueHsv = {
    h: color.hue,
    s: 1,
    v: 1
  };

  const hueColor = new TinyColor(hueHsv).toHexString();

  const hue = color.hue;
  const per = (hue / 360) * 100;

  return (
    <div
      className='relative h-full w-full rounded bg-[linear-gradient(to_right,#ff0000_0%,#ff9900_10%,#cdff00_20%,#35ff00_30%,#00ff66_40%,#00fffd_50%,#0066ff_60%,#3200ff_70%,#cd00ff_80%,#ff0099_90%,#ff0000_100%)]'
      ref={node}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      <span
        className='absolute top-[-3px] -ml-[7px] block size-[14px] cursor-grab rounded-full border-2 border-white py-px shadow-[0_2px_4px_0_rgba(0,0,0,0.24)]'
        style={{ left: `${per}%`, backgroundColor: hueColor }}
      />
      <div className='absolute h-full w-full cursor-grab' />
    </div>
  );
};

export default Ribbon;
